import React, { memo } from 'react';
import * as GStyled from '../../../styles/components';
import * as Styled from './styled';
import { ReportSnapshot, ReportType } from '../../../models';
import ReportSettingRollforward from './ReportSettingRollforward';
import ReportSettingMaturity from './ReportSettingMaturity';
import ReportSettingCommitment from './ReportSettingCommitment';
import ReportSettingFiscalRollforward from './ReportSettingFiscalRollforward';
import ReportSettingPercentageRent from './ReportSettingPercentageRent';
import ReportSettingStartEnd from './ReportSettingStartEnd';
import ReportSettingSubLease from './ReportSettingSubLease';
import ReportSettingWeightedAverage from './ReportSettingWeightedAverage';
import ReportSettingDisclosure from './ReportSettingDisclosure';
import ReportSettingFromTo from './ReportSettingFromTo';
import ReportSettingDeleteBalance from './ReportSettingDeleteBalance';
import ReportSettingLeaseCommittment from './ReportSettingLeaseCommittment';
import ReportSettingAssetAmort from './ReportSettingAssetAmort';
import ReportSettingCustom from './ReportSettingCustom';
import ReportSettingPAndL from './ReportSettingPAndL';
import ReportSettingAP from './ReportSettingAP';
import ReportSettingInforAP from './ReportSettingInforAP';
import ReportSettingROUAssets from './ReportSettingROUAssets';
import { Icon } from '../../../components';
import { replaceIfrsLabels } from '../../../util/frs102Helper';
import { useIsFrs102Enabled } from '../../../util/hooks/useIsFrs102Enabled';

interface ReportSettingProps {
  reportSnapshot: ReportSnapshot;
}

const renderReportSettingByType = (
  reportSnapshot: ReportSnapshot,
): JSX.Element => {
  switch (reportSnapshot.standardReportTypeDisplay) {
    case ReportType.rollforward:
      return <ReportSettingRollforward reportSnapshot={reportSnapshot} />;
    case ReportType.maturity:
      return <ReportSettingMaturity reportSnapshot={reportSnapshot} />;
    case ReportType.committment:
      return <ReportSettingCommitment reportSnapshot={reportSnapshot} />;
    case ReportType.fiscalRollforward:
      return <ReportSettingFiscalRollforward reportSnapshot={reportSnapshot} />;
    case ReportType.percentageRent:
      return <ReportSettingPercentageRent reportSnapshot={reportSnapshot} />;
    case ReportType.startEnd:
      return <ReportSettingStartEnd reportSnapshot={reportSnapshot} />;
    case ReportType.sublease:
      return <ReportSettingSubLease reportSnapshot={reportSnapshot} />;
    case ReportType.weightedAverage:
      return <ReportSettingWeightedAverage reportSnapshot={reportSnapshot} />;
    case ReportType.disclosure:
      return <ReportSettingDisclosure reportSnapshot={reportSnapshot} />;
    case ReportType.fromTo:
      return <ReportSettingFromTo reportSnapshot={reportSnapshot} />;
    case ReportType.deleteBalance:
      return <ReportSettingDeleteBalance reportSnapshot={reportSnapshot} />;
    case ReportType.leaseCommittment:
      return <ReportSettingLeaseCommittment reportSnapshot={reportSnapshot} />;
    case ReportType.assetAmort:
      return <ReportSettingAssetAmort reportSnapshot={reportSnapshot} />;
    case ReportType.effectOfStandardAdoptionPAndL:
      return <ReportSettingPAndL reportSnapshot={reportSnapshot} />;
    case ReportType.ap:
      return reportSnapshot.reportName === 'Infor AP Report' ? (
        <ReportSettingInforAP reportSnapshot={reportSnapshot} />
      ) : (
        <ReportSettingAP reportSnapshot={reportSnapshot} />
      );
    case ReportType.rouAssets:
      return <ReportSettingROUAssets reportSnapshot={reportSnapshot} />;

    default:
      return <ReportSettingCustom reportSnapshot={reportSnapshot} />;
  }
};

function ReportSetting({ reportSnapshot }: ReportSettingProps): JSX.Element {
  const isFrs102Enabled = useIsFrs102Enabled();
  const { reportName, clientName } = reportSnapshot;

  return (
    <div>
      <Styled.ReportSettingItem>
        <Icon name="report" />
        <GStyled.Text>Report Name</GStyled.Text>
        <GStyled.Text fontWeight="medium">{replaceIfrsLabels(reportName, isFrs102Enabled)}</GStyled.Text>
      </Styled.ReportSettingItem>
      <Styled.ReportSettingItem>
        <Icon name="user" />
        <GStyled.Text>Client</GStyled.Text>
        <GStyled.Text fontWeight="medium">{clientName}</GStyled.Text>
      </Styled.ReportSettingItem>
      {renderReportSettingByType(reportSnapshot)}
    </div>
  );
}

export default memo(ReportSetting);
